import { AppRole } from './auth';

export enum FieldPermission {
  READ = 'R',
  WRITE = 'W',
  HIDDEN = '-',
}

export interface FieldPermissionMatrix {
  [sectionName: string]: {
    [fieldName: string]: {
      [roleName: string]: FieldPermission;
    };
  };
}

export interface FieldPermissionRule {
  section: string;
  field: string;
  role: AppRole;
  permission: FieldPermission;
}

export interface FieldAccessResult {
  canRead: boolean;
  canWrite: boolean;
  isHidden: boolean;
  /**
   * Whether a permission matrix was actually consulted to produce this answer.
   *
   * `false` means "could not determine" — the stage matrix was not loaded yet — and is NOT
   * the same as a matrix that resolved the field to hidden. Distinguishing them matters
   * because the two look identical otherwise: `getDefaultAccess()` returns
   * `isHidden: true`, so a consumer testing `isHidden !== undefined` to decide whether the
   * synchronous lookup succeeded gets `true` on a miss, skips its async fallback, and
   * renders a denial it never actually established.
   *
   * Optional so every existing consumer keeps its current behaviour; only callers that need
   * to fall back should read it.
   */
  resolved?: boolean;
}

// Configuration for field permission service
export interface FieldPermissionConfig {
  defaultPermission: FieldPermission;
  enableCaching: boolean;
  cacheTimeout: number; // in milliseconds
}

// Field permission check context
export interface PermissionCheckContext {
  userRoles: AppRole[];
  section: string;
  field: string;
  requestId?: string;
  userRegion?: string;
  requestStage?: string; // Optional stage for stage-based permissions
  userEmail?: string; // User's email to look up team member access level
  partnerContributorAccessLevel?: 'read' | 'read_write'; // Pre-fetched access level for performance
}
